import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Upload,
  Settings,
  Users,
  Link,
  ListTodo,
  Sun,
  Sunset,
  Target,
  BarChart3,
  AlertTriangle,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CalendarView, TimeFormat } from '../types';
import type { CalendarEventInstance } from '@/lib/api';
import { formatDateLabel } from '../utils';
import { SearchBar } from './SearchBar';

interface CalendarToolbarProps {
  currentDate: Date;
  view: CalendarView;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onViewChange: (view: CalendarView) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onNewEvent: () => void;
  onImport?: () => void;
  onSettings: () => void;
  onFeatures?: () => void;
  onAnalytics?: () => void;
  onFindTime?: () => void;
  onSchedulingLinks?: () => void;
  onToggleTasks?: () => void;
  showTaskPanel?: boolean;
  onDailyPlanning?: () => void;
  onEveningReview?: () => void;
  onFocusTime?: () => void;
  searchQuery?: string;
  onSearchQueryChange?: (q: string) => void;
  searchResults?: CalendarEventInstance[];
  searchLoading?: boolean;
  onSearchSelect?: (event: CalendarEventInstance) => void;
  onSearchDeleteEvents?: (eventIds: string[]) => Promise<void>;
  onSearchClear?: () => void;
  timeFormat?: TimeFormat;
  meetingWarning?: { count: number; max: number } | null;
}

interface ToolbarAction {
  key: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  /** Rendered as a pressed icon button and a checked menu item. */
  active?: boolean;
}

export function CalendarToolbar({
  currentDate,
  view,
  sidebarCollapsed = false,
  onToggleSidebar,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  onNewEvent,
  onImport,
  onSettings,
  onFeatures,
  onAnalytics,
  onFindTime,
  onSchedulingLinks,
  onToggleTasks,
  showTaskPanel,
  onDailyPlanning,
  onEveningReview,
  onFocusTime,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  searchLoading,
  onSearchSelect,
  onSearchDeleteEvents,
  onSearchClear,
  timeFormat,
  meetingWarning,
}: CalendarToolbarProps) {
  // One list, rendered twice: as an icon row where there is room for it, and
  // as an overflow menu where there is not. Declaring the actions once is what
  // keeps the two in step — the icon row used to be the only rendering, and on
  // a phone its tail simply ran off the edge of the screen.
  const actions: ToolbarAction[] = [];
  if (onDailyPlanning) {
    actions.push({ key: 'plan', label: 'Plan my day', icon: Sun, onClick: onDailyPlanning });
  }
  if (onEveningReview) {
    actions.push({
      key: 'evening',
      label: 'Evening review',
      icon: Sunset,
      onClick: onEveningReview,
    });
  }
  if (onFocusTime) {
    actions.push({ key: 'focus', label: 'Focus Time', icon: Target, onClick: onFocusTime });
  }
  if (onToggleTasks) {
    actions.push({
      key: 'tasks',
      label: 'Tasks',
      icon: ListTodo,
      onClick: onToggleTasks,
      active: showTaskPanel,
    });
  }
  if (onSchedulingLinks) {
    actions.push({
      key: 'links',
      label: 'Scheduling Links',
      icon: Link,
      onClick: onSchedulingLinks,
    });
  }
  if (onFindTime) {
    actions.push({ key: 'find', label: 'Find a Time', icon: Users, onClick: onFindTime });
  }
  if (onAnalytics) {
    actions.push({ key: 'analytics', label: 'Analytics', icon: BarChart3, onClick: onAnalytics });
  }
  if (onImport) {
    actions.push({ key: 'import', label: 'Import', icon: Upload, onClick: onImport });
  }
  // Features and Settings are never gated: they are how a hidden feature gets
  // turned back on.
  if (onFeatures) {
    actions.push({
      key: 'features',
      label: 'Features',
      icon: SlidersHorizontal,
      onClick: onFeatures,
    });
  }
  actions.push({ key: 'settings', label: 'Settings', icon: Settings, onClick: onSettings });

  return (
    <div className="flex items-center justify-between border-b px-2 sm:px-4 py-2 gap-x-2 gap-y-1 flex-wrap">
      {/* Left: Navigation */}
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        {onToggleSidebar && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onToggleSidebar}
            aria-label={sidebarCollapsed ? 'Show calendar list' : 'Hide calendar list'}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? 'Show calendar list' : 'Hide calendar list'}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onToday} className="shrink-0">
          Today
        </Button>
        <div className="flex items-center shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onPrev}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onNext}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h2
          className="text-base sm:text-lg font-semibold truncate"
          title={formatDateLabel(currentDate, view)}
        >
          {formatDateLabel(currentDate, view)}
        </h2>

        {meetingWarning && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">
            <AlertTriangle className="h-3 w-3" />
            {meetingWarning.count}/{meetingWarning.max} meetings today
          </span>
        )}

        {onSearchQueryChange && onSearchSelect && onSearchClear && (
          <SearchBar
            query={searchQuery ?? ''}
            onQueryChange={onSearchQueryChange}
            results={searchResults ?? []}
            loading={searchLoading ?? false}
            onSelectEvent={onSearchSelect}
            onDeleteEvents={onSearchDeleteEvents}
            onClear={onSearchClear}
            timeFormat={timeFormat}
          />
        )}
      </div>

      {/* Right: View tabs + actions */}
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <Tabs value={view} onValueChange={(v) => onViewChange(v as CalendarView)}>
          <TabsList className="h-8">
            <TabsTrigger value="month" className="text-xs px-2 sm:px-3 h-7">
              Month
            </TabsTrigger>
            <TabsTrigger value="week" className="text-xs px-2 sm:px-3 h-7">
              Week
            </TabsTrigger>
            <TabsTrigger value="day" className="text-xs px-2 sm:px-3 h-7">
              Day
            </TabsTrigger>
            <TabsTrigger value="agenda" className="text-xs px-2 sm:px-3 h-7">
              Agenda
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Wide: every action as its own icon button */}
        <div className="hidden lg:flex items-center gap-2">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={action.active ? 'secondary' : 'ghost'}
              size="icon"
              className="h-8 w-8"
              onClick={action.onClick}
              title={action.label}
              aria-label={action.label}
            >
              <action.icon className="h-4 w-4" />
            </Button>
          ))}
        </div>

        {/* Narrow: the same actions behind one button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 lg:hidden"
              aria-label="More actions"
              title="More actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action) => (
              <DropdownMenuItem key={action.key} onClick={action.onClick}>
                <action.icon className="h-3.5 w-3.5 mr-2" />
                {action.label}
                {action.active && <span className="ml-auto pl-2 text-xs">On</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button size="sm" onClick={onNewEvent} className="shrink-0">
          <Plus className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">New Event</span>
          <span className="sr-only sm:hidden">New Event</span>
        </Button>
      </div>
    </div>
  );
}
